import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import useIsMobile from '../../../../helpers/useIsMobile'
import { toneOf } from './liveUi'

// Large-button sheet for choice / menu / drug taps.
// options: [{ key, label, sublabel, tone, value }]
// allowOther: show a free-text "Other" drug form (name / dose / route)
const PickerDialog = ({
  open,
  title,
  hint,
  options = [],
  tone,
  onPick,
  onClose,
  allowOther = false,
}) => {
  const isMobile = useIsMobile('sm')
  const [other, setOther] = useState(null)

  useEffect(() => {
    if (!open) setOther(null)
  }, [open])

  const colours = toneOf(tone)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{ bgcolor: colours.main, color: '#fff', fontWeight: 800 }}
      >
        {title}
        {hint && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {hint}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {other ? (
          <Stack gap={2}>
            <TextField
              label="Medicine"
              value={other.name}
              autoFocus
              onChange={(e) =>
                setOther((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <Stack direction="row" gap={1}>
              <TextField
                label="Dose"
                value={other.dose}
                onChange={(e) =>
                  setOther((prev) => ({ ...prev, dose: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Route"
                value={other.route}
                onChange={(e) =>
                  setOther((prev) => ({ ...prev, route: e.target.value }))
                }
                fullWidth
              />
            </Stack>
            <Button
              variant="contained"
              color="success"
              size="large"
              disabled={!other.name.trim()}
              onClick={() =>
                onPick({
                  key: 'other',
                  value: {
                    name: other.name.trim(),
                    dose: other.dose.trim(),
                    route: other.route.trim(),
                  },
                })
              }
              sx={{ minHeight: 56 }}
            >
              Log medicine
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 1.25,
            }}
          >
            {options.map((option) => {
              const optionTone = toneOf(option.tone || tone)
              return (
                <ButtonBase
                  key={option.key}
                  onClick={() => onPick(option)}
                  sx={{
                    minHeight: 88,
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: optionTone.bg,
                    color: optionTone.dark,
                    border: 2,
                    borderColor: optionTone.main,
                    flexDirection: 'column',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    fontSize="1.15rem"
                    fontWeight={800}
                    lineHeight={1.2}
                  >
                    {option.label}
                  </Typography>
                  {option.sublabel && (
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {option.sublabel}
                    </Typography>
                  )}
                </ButtonBase>
              )
            })}
            {allowOther && (
              <ButtonBase
                onClick={() => setOther({ name: '', dose: '', route: '' })}
                sx={{
                  minHeight: 88,
                  borderRadius: 2,
                  p: 1.5,
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography fontSize="1.15rem" fontWeight={800}>
                  Other…
                </Typography>
              </ButtonBase>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {other && <Button onClick={() => setOther(null)}>Back</Button>}
        <Button onClick={onClose} size="large">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PickerDialog
